import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, TrendUp, TrendDown, CheckCircle, Lock, Trophy, Clock, Warning, Binoculars, Bank, ChartLineDown } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { showXpToasts } from '../lib/progression';
import { useAuth } from '../context/AuthContext';
import TeenVestLoader from '../components/TeenVestLoader';

const ORACLE_ICON = 'https://static.prod-images.emergentagent.com/jobs/fa067ad4-d472-4c3c-ad71-9aa2aa1140ae/images/8ba4739ef8e21669b51723b2229507cf117bffe01dff02a8f86b687a101a716b.png';

// Real-world event scenarios with real companies
const ORACLE_EVENTS = [
  { title: 'Iran-US Military Tensions', desc: 'Military escalation in the Middle East. Oil prices spike, defense stocks rally, travel stocks plunge.', companies: [
    { symbol: 'LMT', name: 'Lockheed Martin', sector: 'Defense', basePrice: 520, outcome: 'up', outcomeMsg: 'Defense contracts surged +18%', metrics: { revenue: '+12%', margin: '14%', debt: 'Low' } },
    { symbol: 'XOM', name: 'Exxon Mobil', sector: 'Energy', basePrice: 115, outcome: 'up', outcomeMsg: 'Oil prices pushed XOM up +14%', metrics: { revenue: '+8%', margin: '11%', debt: 'Moderate' } },
    { symbol: 'DAL', name: 'Delta Airlines', sector: 'Travel', basePrice: 48, outcome: 'down', outcomeMsg: 'Travel fears sank airlines -22%', metrics: { revenue: '+5%', margin: '8%', debt: 'High' } },
  ]},
  { title: 'AI Speculation Frenzy', desc: 'Every company adds "AI" to their name. Valuations skyrocket without revenue to back them up.', companies: [
    { symbol: 'NVDA', name: 'NVIDIA', sector: 'Tech', basePrice: 890, outcome: 'up', outcomeMsg: 'Real AI revenue justified +25% gain', metrics: { revenue: '+122%', margin: '57%', debt: 'Low' } },
    { symbol: 'SOUN', name: 'SoundHound AI', sector: 'Tech', basePrice: 8, outcome: 'down', outcomeMsg: 'No real revenue — crashed -60%', metrics: { revenue: '-2%', margin: '-45%', debt: 'High' } },
    { symbol: 'MSFT', name: 'Microsoft', sector: 'Tech', basePrice: 420, outcome: 'up', outcomeMsg: 'Azure AI revenue drove +15%', metrics: { revenue: '+18%', margin: '42%', debt: 'Low' } },
  ]},
  { title: 'Federal Reserve Rate Shock', desc: 'Fed surprises with 100bps emergency rate hike. Banks benefit, tech growth stocks suffer.', companies: [
    { symbol: 'JPM', name: 'JPMorgan Chase', sector: 'Finance', basePrice: 198, outcome: 'up', outcomeMsg: 'Higher rates = bigger margins +12%', metrics: { revenue: '+9%', margin: '35%', debt: 'Low' } },
    { symbol: 'SHOP', name: 'Shopify', sector: 'Tech', basePrice: 72, outcome: 'down', outcomeMsg: 'Growth stocks crushed by rates -30%', metrics: { revenue: '+25%', margin: '-3%', debt: 'Moderate' } },
    { symbol: 'GS', name: 'Goldman Sachs', sector: 'Finance', basePrice: 450, outcome: 'up', outcomeMsg: 'Trading revenue surged +18%', metrics: { revenue: '+14%', margin: '28%', debt: 'Low' } },
  ]},
  { title: 'Pandemic Fears Return', desc: 'New virus variant sparks global lockdown fears. Stay-at-home stocks rally, travel crashes.', companies: [
    { symbol: 'ZM', name: 'Zoom Video', sector: 'Tech', basePrice: 68, outcome: 'up', outcomeMsg: 'Remote work demand pushed +35%', metrics: { revenue: '+8%', margin: '22%', debt: 'None' } },
    { symbol: 'PFE', name: 'Pfizer', sector: 'Healthcare', basePrice: 28, outcome: 'up', outcomeMsg: 'Vaccine hopes drove +20%', metrics: { revenue: '+5%', margin: '25%', debt: 'Low' } },
    { symbol: 'UAL', name: 'United Airlines', sector: 'Travel', basePrice: 42, outcome: 'down', outcomeMsg: 'Bookings collapsed -35%', metrics: { revenue: '+12%', margin: '6%', debt: 'Very High' } },
  ]},
  { title: 'China Trade War Escalation', desc: 'US slaps 100% tariffs on Chinese goods. Supply chains disrupted globally.', companies: [
    { symbol: 'AAPL', name: 'Apple', sector: 'Tech', basePrice: 190, outcome: 'down', outcomeMsg: 'iPhone production costs soared -18%', metrics: { revenue: '+6%', margin: '44%', debt: 'Low' } },
    { symbol: 'BABA', name: 'Alibaba', sector: 'E-Commerce', basePrice: 82, outcome: 'down', outcomeMsg: 'Chinese stocks hammered -40%', metrics: { revenue: '+14%', margin: '15%', debt: 'Moderate' } },
    { symbol: 'WMT', name: 'Walmart', sector: 'Retail', basePrice: 165, outcome: 'up', outcomeMsg: 'Domestic focus helped +8%', metrics: { revenue: '+4%', margin: '3.5%', debt: 'Low' } },
  ]},
  { title: 'Crypto Regulation Crackdown', desc: 'SEC bans most crypto trading for retail investors. Crypto-adjacent stocks plummet.', companies: [
    { symbol: 'COIN', name: 'Coinbase', sector: 'Crypto', basePrice: 225, outcome: 'down', outcomeMsg: 'Trading volume collapsed -45%', metrics: { revenue: '+30%', margin: '12%', debt: 'Low' } },
    { symbol: 'SQ', name: 'Block (Square)', sector: 'Fintech', basePrice: 75, outcome: 'down', outcomeMsg: 'Bitcoin revenue hit hard -25%', metrics: { revenue: '+18%', margin: '5%', debt: 'Moderate' } },
    { symbol: 'V', name: 'Visa', sector: 'Finance', basePrice: 280, outcome: 'up', outcomeMsg: 'Traditional payments benefit +10%', metrics: { revenue: '+11%', margin: '65%', debt: 'Low' } },
  ]},
  { title: 'European Energy Crisis', desc: 'Natural gas shortages cripple European industry. Energy stocks surge, manufacturing falls.', companies: [
    { symbol: 'CVX', name: 'Chevron', sector: 'Energy', basePrice: 160, outcome: 'up', outcomeMsg: 'LNG exports drove +20%', metrics: { revenue: '+22%', margin: '15%', debt: 'Low' } },
    { symbol: 'TSLA', name: 'Tesla', sector: 'Auto', basePrice: 245, outcome: 'up', outcomeMsg: 'EV demand surged on gas prices +15%', metrics: { revenue: '+28%', margin: '18%', debt: 'Low' } },
    { symbol: 'BMW', name: 'BMW', sector: 'Auto', basePrice: 95, outcome: 'down', outcomeMsg: 'German manufacturing halted -28%', metrics: { revenue: '+3%', margin: '8%', debt: 'High' } },
  ]},
  { title: 'Housing Market Collapse', desc: 'Mortgage rates hit 12%. Housing starts plummet. Banks face rising defaults.', companies: [
    { symbol: 'HD', name: 'Home Depot', sector: 'Retail', basePrice: 340, outcome: 'down', outcomeMsg: 'Home renovation spending crashed -20%', metrics: { revenue: '-5%', margin: '14%', debt: 'Moderate' } },
    { symbol: 'BAC', name: 'Bank of America', sector: 'Finance', basePrice: 35, outcome: 'down', outcomeMsg: 'Mortgage defaults surged -22%', metrics: { revenue: '+2%', margin: '28%', debt: 'High' } },
    { symbol: 'COST', name: 'Costco', sector: 'Retail', basePrice: 580, outcome: 'up', outcomeMsg: 'Defensive spending held strong +5%', metrics: { revenue: '+8%', margin: '3%', debt: 'Low' } },
  ]},
  { title: 'Tech Antitrust Breakup', desc: 'Government orders breakup of major tech monopolies. Big tech falls, small caps rally.', companies: [
    { symbol: 'GOOGL', name: 'Alphabet', sector: 'Tech', basePrice: 140, outcome: 'down', outcomeMsg: 'Breakup fears crushed -25%', metrics: { revenue: '+15%', margin: '30%', debt: 'None' } },
    { symbol: 'META', name: 'Meta', sector: 'Tech', basePrice: 500, outcome: 'down', outcomeMsg: 'Instagram/WhatsApp spinoff fears -20%', metrics: { revenue: '+22%', margin: '35%', debt: 'Low' } },
    { symbol: 'SNAP', name: 'Snap Inc', sector: 'Tech', basePrice: 12, outcome: 'up', outcomeMsg: 'Smaller players benefit +30%', metrics: { revenue: '+18%', margin: '-8%', debt: 'Moderate' } },
  ]},
  { title: 'Global Food Crisis', desc: 'Droughts and war disrupt wheat and corn supplies worldwide. Agriculture stocks soar.', companies: [
    { symbol: 'ADM', name: 'Archer-Daniels', sector: 'Agriculture', basePrice: 78, outcome: 'up', outcomeMsg: 'Food commodity prices drove +22%', metrics: { revenue: '+18%', margin: '8%', debt: 'Low' } },
    { symbol: 'DE', name: 'John Deere', sector: 'Agriculture', basePrice: 420, outcome: 'up', outcomeMsg: 'Farm equipment demand surged +16%', metrics: { revenue: '+12%', margin: '20%', debt: 'Moderate' } },
    { symbol: 'MCD', name: "McDonald's", sector: 'Food', basePrice: 290, outcome: 'down', outcomeMsg: 'Input costs soared -12%', metrics: { revenue: '+4%', margin: '32%', debt: 'High' } },
  ]},
];

const getLevelEvent = (level) => ORACLE_EVENTS[(level - 1) % ORACLE_EVENTS.length];

const getLevelConfig = (level) => {
  if (level <= 10) return { timeToDecide: 60, revealDelay: 3 };
  if (level <= 20) return { timeToDecide: 45, revealDelay: 3 };
  if (level <= 30) return { timeToDecide: 35, revealDelay: 2 };
  if (level <= 40) return { timeToDecide: 25, revealDelay: 2 };
  return { timeToDecide: 20, revealDelay: 1 };
};

const OracleMode = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [screen, setScreen] = useState('levels'); // levels, playing, reveal, result
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState({ current_level: 1, completed: [], reward_claimed: false });

  const [event, setEvent] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const timerRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await api.get('/gamemode/oracle/progress');
      setProgress(res.data.progress || { current_level: 1, completed: [], reward_claimed: false });
    } catch (e) {
      console.warn('[OracleMode] Failed to fetch progress:', e?.message || e);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const startLevel = (lvl) => {
    const ev = getLevelEvent(lvl);
    const config = getLevelConfig(lvl);
    setLevel(lvl);
    setEvent(ev);
    setDecisions({});
    setRevealed(false);
    setScore(0);
    setTimeLeft(config.timeToDecide);
    setScreen('playing');
  };

  // Timer
  useEffect(() => {
    if (screen !== 'playing') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(p => {
        if (p <= 1) {
          clearInterval(timerRef.current);
          handleSubmitDecisions();
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [screen]);

  const setDecision = (symbol, decision) => {
    setDecisions(p => ({ ...p, [symbol]: decision }));
  };

  const handleSubmitDecisions = () => {
    clearInterval(timerRef.current);
    setScreen('reveal');

    let correct = 0;
    event.companies.forEach(c => {
      const d = decisions[c.symbol];
      if ((d === 'invest' && c.outcome === 'up') || (d === 'avoid' && c.outcome === 'down')) {
        correct++;
      }
    });
    setScore(correct);

    // Reveal with animation delay
    setTimeout(() => setRevealed(true), 1500);
  };

  const handleComplete = async () => {
    const needed = Math.ceil(event.companies.length * 0.5);
    const passed = score >= needed;

    if (passed) {
      try {
        const res = await api.post('/gamemode/oracle/complete-level', { level });
        if (res.data.reward > 0) {
          toast.success(`All 50 levels complete! +$${res.data.reward.toLocaleString()} added to portfolio!`);
        }
        showXpToasts(res.data.xp_result, { refreshUser: fetchUser });
        await fetchProgress();
      } catch (e) {
        console.warn('[OracleMode] complete-level failed:', e?.message || e);
      }
    }
    setScreen('result');
  };

  // =================== LEVELS SCREEN ===================
  if (screen === 'levels') {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/game-modes')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} /> Back to Game Modes
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-5 mb-8">
            <img src={ORACLE_ICON} alt="Oracle" className="w-20 h-20 rounded-2xl object-cover" />
            <div>
              <h1 className="text-3xl font-unbounded font-bold text-white">Oracle Mode</h1>
              <p className="text-zinc-400">Think like Warren Buffett. Analyze real events, predict outcomes, build wealth patiently.</p>
              <p className="text-zinc-500 text-sm mt-1">{progress.completed?.length || 0}/50 completed — Complete all for <span className="text-gold font-bold">+$3,000</span></p>
            </div>
          </motion.div>

          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-6">
            {Array.from({ length: 50 }, (_, i) => i + 1).map(lvl => {
              const done = progress.completed?.includes(lvl);
              const unlocked = lvl <= (progress.current_level || 1);
              const isCurrent = lvl === (progress.current_level || 1) && !done;
              return (
                <motion.button key={lvl} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: lvl * 0.01 }}
                  disabled={!unlocked} onClick={() => unlocked && startLevel(lvl)}
                  className={`aspect-square rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                    done ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400' :
                    isCurrent ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400 animate-pulse' :
                    unlocked ? 'bg-zinc-800 border border-zinc-700 text-white hover:border-amber-500/50' :
                    'bg-zinc-900/30 border border-zinc-800/50 text-zinc-700'
                  }`}
                  data-testid={`oracle-level-${lvl}`}
                >
                  {done ? <CheckCircle size={18} weight="fill" /> : !unlocked ? <Lock size={14} /> : lvl}
                </motion.button>
              );
            })}
          </div>

          <div className="flex gap-3 text-xs text-zinc-500">
            <span>Lv 1-10: 60s</span><span>Lv 11-20: 45s</span><span>Lv 21-30: 35s</span><span>Lv 31-40: 25s</span><span>Lv 41-50: 20s</span>
          </div>
        </div>
      </div>
    );
  }

  // =================== PLAYING SCREEN ===================
  if (screen === 'playing' && event) {
    const allDecided = event.companies.every(c => decisions[c.symbol]);
    return (
      <div className="min-h-screen noise-bg">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-amber-400 text-sm font-bold">Oracle Mode — Level {level}</p>
              <h2 className="text-2xl font-unbounded font-bold text-white">{event.title}</h2>
              <p className="text-zinc-400 text-sm mt-1">{event.desc}</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${timeLeft <= 10 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-zinc-800 text-white'}`}>
              <Clock size={18} /><span className="font-mono font-bold">{timeLeft}s</span>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {event.companies.map((company, i) => (
              <motion.div key={company.symbol} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
                className="glass-card p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-bold text-lg">{company.symbol}</h3>
                      <span className="text-zinc-500 text-sm">{company.name}</span>
                      <span className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">{company.sector}</span>
                    </div>
                    <p className="text-zinc-400 text-sm mt-1">Current Price: <span className="text-white font-mono">${company.basePrice}</span></p>
                  </div>
                </div>

                {/* Metrics */}
                <div className="flex gap-4 mb-4">
                  <div className="glass px-3 py-2 rounded-lg"><p className="text-zinc-500 text-xs">Revenue Growth</p><p className="text-white text-sm font-bold">{company.metrics.revenue}</p></div>
                  <div className="glass px-3 py-2 rounded-lg"><p className="text-zinc-500 text-xs">Profit Margin</p><p className="text-white text-sm font-bold">{company.metrics.margin}</p></div>
                  <div className="glass px-3 py-2 rounded-lg"><p className="text-zinc-500 text-xs">Debt Level</p><p className="text-white text-sm font-bold">{company.metrics.debt}</p></div>
                </div>

                {/* Decision buttons */}
                <div className="flex gap-2">
                  {['invest', 'avoid', 'watch'].map(d => (
                    <Button key={d} onClick={() => setDecision(company.symbol, d)}
                      className={`flex-1 capitalize ${decisions[company.symbol] === d
                        ? d === 'invest' ? 'bg-emerald-600 text-white' : d === 'avoid' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                      data-testid={`oracle-${company.symbol}-${d}`}
                    >
                      {d === 'invest' && <TrendUp size={16} className="mr-1" />}
                      {d === 'avoid' && <TrendDown size={16} className="mr-1" />}
                      {d === 'watch' && <Eye size={16} className="mr-1" />}
                      {d}
                    </Button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <Button onClick={handleSubmitDecisions} disabled={!allDecided}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold text-lg disabled:opacity-50"
            data-testid="oracle-submit"
          >
            {allDecided ? 'Submit Decisions & See Outcome' : `Make all ${event.companies.length} decisions first`}
          </Button>
        </div>
      </div>
    );
  }

  // =================== REVEAL + RESULT ===================
  if ((screen === 'reveal' || screen === 'result') && event) {
    const needed = Math.ceil(event.companies.length * 0.5);
    const passed = score >= needed;

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-8">
          {screen === 'reveal' && !revealed && (
            <div className="text-center py-20">
              <TeenVestLoader size={112} label="Simulating 6 months into the future..." />
            </div>
          )}

          {(revealed || screen === 'result') && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="text-center mb-8">
                <div className={`inline-flex p-5 rounded-full mb-4 ${passed ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  {passed ? <Trophy size={48} weight="fill" className="text-emerald-400" /> : <Warning size={48} weight="fill" className="text-red-400" />}
                </div>
                <h2 className="text-2xl font-unbounded font-bold text-white">
                  Level {level} — {passed ? 'Passed!' : 'Failed'}
                </h2>
                <p className="text-zinc-400">{score}/{event.companies.length} correct decisions (need {needed})</p>
              </div>

              <div className="space-y-4 mb-8">
                {event.companies.map((c, i) => {
                  const d = decisions[c.symbol];
                  const correct = (d === 'invest' && c.outcome === 'up') || (d === 'avoid' && c.outcome === 'down');
                  return (
                    <motion.div key={c.symbol} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.2 }}
                      className={`glass-card p-5 border-l-4 ${correct ? 'border-l-emerald-500' : d === 'watch' ? 'border-l-blue-500' : 'border-l-red-500'}`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-bold">{c.symbol}</h3>
                          <span className="text-zinc-500 text-sm">{c.name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${correct ? 'bg-emerald-500/20 text-emerald-400' : d === 'watch' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>
                            {correct ? 'Correct!' : d === 'watch' ? 'Watched' : 'Wrong'}
                          </span>
                        </div>
                        <span className={`text-sm font-bold ${c.outcome === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {c.outcome === 'up' ? 'Went UP' : 'Went DOWN'}
                        </span>
                      </div>
                      <p className="text-zinc-300 text-sm">{c.outcomeMsg}</p>
                      <p className="text-zinc-500 text-xs mt-1">Your decision: <span className="capitalize text-zinc-400">{d || 'none'}</span></p>
                    </motion.div>
                  );
                })}
              </div>

              {screen === 'reveal' && (
                <Button onClick={handleComplete} className="w-full py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold" data-testid="oracle-continue">
                  Continue
                </Button>
              )}

              {screen === 'result' && (
                <div className="flex gap-4">
                  <Button onClick={() => startLevel(level)} className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700">
                    {passed ? 'Replay' : 'Retry'} Level {level}
                  </Button>
                  {passed && level < 50 ? (
                    <Button onClick={() => startLevel(level + 1)} className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold" data-testid="oracle-next">
                      Next Level <ArrowRight size={18} className="ml-2" />
                    </Button>
                  ) : (
                    <Button onClick={() => setScreen('levels')} className="flex-1 py-4 bg-amber-600 hover:bg-amber-700">
                      Back to Levels
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default OracleMode;
